import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Send, Plus, Link2, Clock, Copy, Check, KeyRound, Download, CornerDownRight } from "lucide-react";
import { RegisterToolbar, SummaryTiles } from "../components/RegisterToolbar";
import { by, downloadCsv, matchesSearch, type SortOption } from "../lib/registerExport";
import { CountdownChip, useNow } from "../components/SubmissionDeadline";
import {
  SubmissionChecklist, SubmissionTimeline, liveCompleteness, type CompletenessSection, type TimelineEvent,
} from "../components/SubmissionTimeline";
import { theme, card, btnPrimary, type as text, pageHeading, pageSubtitle } from "../theme";
import { api, apiBlob, apiErrorMessage } from "../api/client";
import { saveBlob, fileNameSafe } from "../lib/download";
import { LoadingBlock } from "../components/Spinner";
import {
  EmptyState, Field, ModalOverlay, ModalHeader, StatusPill, input, textarea, secondaryBtn, dangerBtn, fmtDay, fmtDateTime,
  toLocalInput, fromInput, relativeDue, type SyncResult,
} from "./resilience/shared";
import { SyncNotice } from "./resilience/IncidentsTab";
import { KIND_HREFS } from "../components/EntityTags";
import { RelatedRecords } from "../components/RelatedRecords";
import { NextSteps, type NextStepItem } from "../components/NextSteps";
import { DetailFields, DetailSection, EditButton, RecordActions, type DetailField } from "../components/RecordView";
import { useCurrentUser } from "../auth/useCurrentUser";
import { canChangeSubmission, isSendingChange } from "../lib/permissions";

// Regulatory submissions — the thing you actually send a competent authority.
//
// Nothing here submits: no regulator publishes an inbound API. The platform produces the artefact,
// records that it was sent and holds the reference that comes back, which is what an audit asks
// for. Saving a draft and downloading its PDF never wait on validation — a notification that is late
// because a field was missing is worse than one that is incomplete — but "ready" is refused by the
// API while a required field is empty.

const KINDS = ["incident_notification", "registration", "annual_return", "assessment_return", "evidence_pack", "information_notice"] as const;
const STATUSES = ["draft", "ready", "submitted", "acknowledged", "withdrawn"] as const;

interface FieldDef { key: string; label: string; required: boolean; basis: string; kind: string; choices?: string[] }
interface Issue { field: string | null; message: string }
interface Completeness {
  hasFieldSet: boolean; required: number; filled: number; percent: number;
  missing: Array<{ key: string; label: string; section: string }>; sections: CompletenessSection[];
}
interface Submission {
  id: string; displayId: number | null; kind: string; regime: string | null; authority: string;
  // Whether this member may send this one: a Data Protection Officer only to a data protection
  // authority (backend middleware/permissions.ts SEND_SCOPE_BY_ROLE). Absent on older responses.
  authorityKind?: string | null; canSend?: boolean;
  // The catalogue entry it is addressed to (GET /submissions/authorities), set by the server. Null
  // for an authority named by hand and for rows written before keys were stored.
  authorityKey?: string | null;
  status: string; payload: Record<string, unknown>; awareAt: string | null; dueAt: string | null;
  dueBasis: string | null; submittedAt: string | null; reference: string | null;
  acknowledgedAt: string | null; method: string | null; createdAt: string;
  urgency: string | null; issues: Issue[];
  completeness: Completeness;
  acknowledgement: { awaiting: boolean; chase: boolean; days: number | null };
  incident: { id: string; displayId: number | null; title: string } | null;
  incidentId?: string | null; notes?: string | null;
  // The incident's notification duty this submission answers (recording it as sent records the duty too).
  notificationId?: string | null;
  notification?: { id: string; body: string; status: string; sentAt: string | null; reference: string | null } | null;
  // Who recorded it as sent and acknowledged (single-submission read only).
  submittedByName?: string | null; acknowledgedByName?: string | null;
}

const isPending = (s: { status: string }) => s.status === "draft" || s.status === "ready";
/** What the authority was told: final once sent, and still closed if sent and then withdrawn (lib/regulatorySubmissions.ts sentRecordError). */
const isSent = (s: { status: string }) => s.status === "submitted" || s.status === "acknowledged";
const wasSent = (s: { status: string; submittedAt: string | null }) => isSent(s) || (s.status === "withdrawn" && !!s.submittedAt);

// The names an authority's name is matched on when finding a submission's addressee in the
// catalogue: the name without an " — role" suffix or a trailing "(ABBR)", and the abbreviation
// itself, ignoring case and spacing. "JOIC — data protection authority" (how an incident addresses
// it) and "Jersey Office of the Information Commissioner (JOIC)" (the catalogue) then meet on "joic".
const authorityMatchNames = (name: string): string[] => {
  const base = name.split(/\s+[—–-]\s+/)[0].trim();
  const abbr = /\(([^)]+)\)\s*$/.exec(base)?.[1];
  const norm = (v: string) => v.replace(/\s+/g, " ").trim().toLowerCase();
  return [norm(base.replace(/\s*\([^)]*\)\s*$/, "")), ...(abbr ? [norm(abbr)] : [])].filter(Boolean);
};
const subRef = (s: { displayId: number | null }) => (s.displayId !== null ? `SUB-${s.displayId}` : "");
const incidentName = (i: Submission["incident"]) => (i ? `${i.displayId != null ? `INC-${String(i.displayId).padStart(3, "0")} ` : ""}${i.title}` : "");
const day = (v: string | null) => (v ? v.slice(0, 10) : "");

type TileKey = "drafts" | "due24" | "overdue" | "awaiting" | "ackYear";

const SORTS: SortOption<Submission>[] = [
  { key: "due", label: "Due soonest", compare: by.date<Submission>((s) => s.dueAt, "asc") },
  { key: "created", label: "Newest first", compare: by.date<Submission>((s) => s.createdAt, "desc") },
  { key: "status", label: "Status", compare: by.rank<Submission>((s) => s.status, STATUSES) },
  { key: "authority", label: "Authority", compare: by.text<Submission>((s) => s.authority) },
];

/** The submission's own fields, beside the payload the authority's form asks for. */
type Meta = {
  authority: string; regime: string; awareAt: string; dueAt: string; dueBasis: string;
  incidentId: string; method: string; reference: string; notes: string;
};
const METHODS = ["portal", "email", "post", "api"] as const;
/** How each method reads on screen (an enum key run through words() gave "Api"). */
const METHOD_LABELS: Record<string, string> = { portal: "Online portal", email: "Email", post: "Post", api: "API (electronic submission)" };
const methodLabel = (m: string) => METHOD_LABELS[m] ?? words(m);

const metaOf = (s: Submission): Meta => ({
  authority: s.authority, regime: s.regime ?? "", awareAt: toLocalInput(s.awareAt), dueAt: toLocalInput(s.dueAt),
  dueBasis: s.dueBasis ?? "", incidentId: s.incidentId ?? "", method: s.method ?? "", reference: s.reference ?? "", notes: s.notes ?? "",
});

/** An ISO date-time as the datetime-local input shows it; anything else is shown as typed. */
const dateForInput = (v: string) => (/^\d{4}-\d{2}-\d{2}T.*(Z|[+-]\d{2}:\d{2})$/.test(v) ? toLocalInput(v) : v);

/**
 * What the submission still lacks, from the same required-field rule the API applies
 * (lib/regulatorySubmissions.ts validateSubmission), worked out as the form is filled in,
 * then the status ladder: ready, sent, the authority's reference, acknowledged.
 */
function submissionSteps(s: Submission, fields: FieldDef[], payload: Record<string, string>, meta: Meta): NextStepItem[] {
  if (s.status === "withdrawn") return [];
  const overdue = !!meta.dueAt && new Date(meta.dueAt).getTime() < Date.now() && (s.status === "draft" || s.status === "ready");
  const missing = fields.filter((f) => f.required && !(payload[f.key] ?? "").trim());
  const sent = s.status === "submitted" || s.status === "acknowledged";
  return [
    ...missing.map((f) => ({ done: false, label: `Fill in: ${f.label}`, hint: f.basis || undefined, urgent: overdue })),
    ...(missing.length === 0 && fields.some((f) => f.required) ? [{ done: true, label: "Every required field answered" }] : []),
    { done: !!meta.dueAt || sent, label: "Set when it is due", hint: "The deadline clock and the reminders run from it." },
    { done: s.status !== "draft", label: "Mark it ready to send" },
    { done: sent, label: "Send it and record it as sent", hint: meta.dueAt ? `${relativeDue(fromInput(meta.dueAt))}.` : undefined, urgent: overdue },
    ...(sent ? [
      { done: !!meta.reference.trim(), label: "Record the authority's reference", hint: "Its absence is the evidence gap an audit asks about." },
      { done: s.status === "acknowledged", label: "Record the authority's acknowledgement" },
    ] : []),
  ];
}
interface Share {
  id: string; recipientLabel: string; recipientKey: string | null; expiresAt: string;
  revokedAt: string | null; viewCount: number; lastViewedAt: string | null;
  failedAttempts: number; standing: "active" | "withdrawn" | "expired" | "locked";
}
interface Authority { key: string; name: string; kind: string; country: string; basis: string }

// What the organisation sends the authority, and what it keeps back for the second email.
interface ShareCredentials { url: string; passKey: string; recipientLabel: string }

const STANDING_LABEL: Record<string, string> = {
  active: "live", withdrawn: "withdrawn", expired: "expired",
  locked: "locked — too many wrong pass keys",
};

const OTHER = "other";

const words = (s: string) => s.replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase());

export function SubmissionsPage() {
  // Preparing a submission (submissions:prepare) and sending it (submissions:send) are separate
  // permissions (submissions.routes.ts): recording it as sent, recording the acknowledgement,
  // touching a sent record and sharing it with the authority are sending. The view mirrors it.
  const { user } = useCurrentUser();
  const perms = user?.permissions;
  const canPrepare = !!perms?.includes("submissions:prepare");
  const canSend = !!perms?.includes("submissions:send");
  const canEdit = canPrepare || canSend;
  const [searchParams, setSearchParams] = useSearchParams();
  const [submissions, setSubmissions] = useState<Submission[] | null>(null);
  const [open, setOpen] = useState<Submission | null>(null);
  // A submission opens read-only; the payload and its own fields are edited behind Edit.
  const [editing, setEditing] = useState(false);
  const [fields, setFields] = useState<FieldDef[]>([]);
  const [payload, setPayload] = useState<Record<string, string>>({});
  const [shares, setShares] = useState<Share[]>([]);
  const [authorities, setAuthorities] = useState<Authority[]>([]);
  const [kindLabels, setKindLabels] = useState<Record<string, string>>({});
  const [shareKey, setShareKey] = useState("");
  const [shareLabel, setShareLabel] = useState("");
  const [credentials, setCredentials] = useState<ShareCredentials | null>(null);
  const [copied, setCopied] = useState("");
  const [creating, setCreating] = useState(false);
  // authorityKey is a catalogue key, OTHER (named by hand in `authority`), or "" (not chosen yet).
  const [newForm, setNewForm] = useState({ kind: "incident_notification", authorityKey: "", authority: "", regime: "" });
  const [busy, setBusy] = useState(false);
  const [downloading, setDownloading] = useState<string | null>(null);
  const [error, setError] = useState("");
  // What the last save also recorded on the incident and its linked records (PUT /submissions/:id `sync`).
  const [synced, setSynced] = useState<SyncResult | null>(null);
  const [meta, setMeta] = useState<Meta | null>(null);
  const [incidents, setIncidents] = useState<Array<{ id: string; name: string }>>([]);
  const [dueSoonHours, setDueSoonHours] = useState(4);
  const [chaseDays, setChaseDays] = useState(14);
  const [detail, setDetail] = useState<{
    timeline: TimelineEvent[]; sections: CompletenessSection[]; followUpKinds: string[];
    submittedByName?: string | null; acknowledgedByName?: string | null;
  } | null>(null);
  const [tile, setTile] = useState<TileKey | null>(null);
  const [statusFilter, setStatusFilter] = useState("");
  const [kindFilter, setKindFilter] = useState("");
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("due");
  const now = useNow();

  const load = useCallback(async (): Promise<Submission[] | null> => {
    try {
      const res = await api.get<{ submissions: Submission[]; dueSoonHours?: number; chaseDays?: number }>("/submissions");
      setSubmissions(res.submissions);
      if (typeof res.dueSoonHours === "number") setDueSoonHours(res.dueSoonHours);
      if (typeof res.chaseDays === "number") setChaseDays(res.chaseDays);
      return res.submissions;
    } catch (e) {
      setError(apiErrorMessage(e, "Could not load submissions."));
      setSubmissions([]);
      return null;
    }
  }, []);

  useEffect(() => { void load(); }, [load]);
  // The catalogue the new-submission form picks from; opening a submission reads it again.
  useEffect(() => {
    api.get<{ authorities: Authority[]; kindLabels: Record<string, string> }>("/submissions/authorities")
      .then((auth) => { setAuthorities(auth.authorities); setKindLabels(auth.kindLabels); })
      .catch(() => { /* the form still offers "Another authority" */ });
  }, []);
  useEffect(() => {
    api.get<{ incidents: Array<{ id: string; displayId: number | null; title: string }> }>("/resilience/incidents")
      .then((d) => setIncidents(d.incidents.map((i) => ({ id: i.id, name: `${i.displayId != null ? `INC-${String(i.displayId).padStart(3, "0")} ` : ""}${i.title}` }))))
      .catch(() => setIncidents([]));
  }, []);

  // A change of regime changes which fields the authority's form carries.
  const loadFields = useCallback(async (kind: string, regime: string) => {
    try {
      const f = await api.get<{ fields: FieldDef[] }>(`/submissions/fields?kind=${kind}${regime.trim() ? `&regime=${encodeURIComponent(regime.trim())}` : ""}`);
      setFields(f.fields);
    } catch { /* keep the fields already shown */ }
  }, []);

  const openSubmission = useCallback(async (s: Submission) => {
    setOpen(s);
    setEditing(false);
    setError("");
    setSynced(null);
    setCredentials(null);
    setCopied("");
    setShareLabel("");
    setMeta(metaOf(s));
    setPayload(Object.fromEntries(Object.entries(s.payload ?? {}).map(([k, v]) => [k, v === null || v === undefined ? "" : String(v)])));
    setDetail({ timeline: [], sections: s.completeness?.sections ?? [], followUpKinds: [] });
    // The history and what may follow it come with the single-submission read.
    api.get<{ timeline: TimelineEvent[]; followUpKinds: string[]; submission: Pick<Submission, "completeness" | "submittedByName" | "acknowledgedByName"> }>(`/submissions/${s.id}`)
      .then((d) => setDetail({
        timeline: d.timeline, sections: d.submission.completeness.sections, followUpKinds: d.followUpKinds,
        submittedByName: d.submission.submittedByName, acknowledgedByName: d.submission.acknowledgedByName,
      }))
      .catch(() => { /* the list row already carries the checklist */ });
    try {
      const [f, sh, auth] = await Promise.all([
        api.get<{ fields: FieldDef[] }>(`/submissions/fields?kind=${s.kind}${s.regime ? `&regime=${encodeURIComponent(s.regime)}` : ""}`),
        api.get<{ shares: Share[] }>(`/submissions/${s.id}/shares`).catch(() => ({ shares: [] })),
        api.get<{ authorities: Authority[]; kindLabels: Record<string, string> }>("/submissions/authorities")
          .catch(() => ({ authorities: [] as Authority[], kindLabels: {} as Record<string, string> })),
      ]);
      setFields(f.fields);
      setShares(sh.shares);
      setAuthorities(auth.authorities);
      setKindLabels(auth.kindLabels);
      // The submission is already addressed to a body; that is who a share of it normally goes to.
      // When the catalogue does not carry it, the name it was addressed to is offered back rather
      // than making somebody retype it.
      // The stored catalogue key decides. A row without one (named by hand, or written before keys
      // were stored) is matched by name; one drafted from an incident is addressed with the
      // notification's wording, not the catalogue's, so an exact match falls back to the normalised names.
      const targets = new Set(authorityMatchNames(s.authority));
      const addressee = (s.authorityKey ? auth.authorities.find((a) => a.key === s.authorityKey) : undefined)
        ?? auth.authorities.find((a) => a.name === s.authority)
        ?? auth.authorities.find((a) => authorityMatchNames(a.name).some((n) => targets.has(n)));
      setShareKey(addressee ? addressee.key : OTHER);
      if (!addressee) setShareLabel(s.authority);
    } catch (e) { setError(apiErrorMessage(e, "Could not load submissions.")); }
  }, []);

  // ?submission=<id> — where the top bar's deadline clock, the action items and the alert bell
  // all point. Opened once per id so closing the modal does not immediately reopen it.
  const deepLinked = searchParams.get("submission");
  const openedFromLink = useRef<string | null>(null);
  useEffect(() => {
    if (!deepLinked || !submissions || openedFromLink.current === deepLinked) return;
    const target = submissions.find((s) => s.id === deepLinked);
    if (!target) return;
    openedFromLink.current = deepLinked;
    void openSubmission(target);
  }, [deepLinked, submissions, openSubmission]);

  const close = useCallback(() => {
    setOpen(null);
    setEditing(false);
    if (searchParams.has("submission")) {
      const next = new URLSearchParams(searchParams);
      next.delete("submission");
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  async function save(status?: string) {
    if (!open) return;
    setBusy(true); setError("");
    if (meta && !meta.authority.trim()) { setError("Name the authority this goes to."); setBusy(false); return; }
    try {
      // A sent submission is final: only the acknowledgement and the authority's reference are recorded on it.
      const res = await api.put<{ sync?: SyncResult | null }>(`/submissions/${open.id}`, wasSent(open) ? {
        ...(meta ? { reference: meta.reference.trim() || null } : {}),
        ...(status ? { status } : {}),
      } : {
        payload,
        ...(meta ? {
          authority: meta.authority.trim(),
          regime: meta.regime.trim() || null,
          awareAt: fromInput(meta.awareAt),
          dueAt: fromInput(meta.dueAt),
          dueBasis: meta.dueBasis.trim() || null,
          incidentId: meta.incidentId || null,
          method: meta.method || null,
          reference: meta.reference.trim() || null,
          notes: meta.notes.trim() || null,
        } : {}),
        ...(status ? { status } : {}),
      });
      // Back to the view of the record just saved, not the list.
      const rows = await load();
      const fresh = rows?.find((r) => r.id === open.id);
      if (fresh) await openSubmission(fresh); else close();
      if (fresh && res.sync && (res.sync.filled.length || res.sync.skipped.length)) setSynced(res.sync);
    } catch (e) { setError(apiErrorMessage(e, "Could not save the submission.")); } finally { setBusy(false); }
  }

  async function create() {
    const byHand = newForm.authorityKey === OTHER;
    if (!newForm.authorityKey || (byHand && !newForm.authority.trim())) { setError("Choose the authority this goes to, or name it."); return; }
    setBusy(true); setError("");
    try {
      // A picked authority is sent as its key alone: the server takes the name from the catalogue.
      await api.post("/submissions", {
        kind: newForm.kind,
        ...(byHand ? { authority: newForm.authority.trim(), authorityKey: null } : { authorityKey: newForm.authorityKey }),
        regime: newForm.regime.trim() || null,
      });
      setCreating(false);
      setNewForm({ kind: "incident_notification", authorityKey: "", authority: "", regime: "" });
      await load();
    } catch (e) { setError(apiErrorMessage(e, "Could not create the submission.")); } finally { setBusy(false); }
  }

  // Only a submission that never went to the authority can be deleted (the API refuses the rest).
  async function remove() {
    if (!open || wasSent(open)) return;
    if (!window.confirm(`Delete ${subRef(open) || "this submission"}? This cannot be undone.`)) return;
    setBusy(true); setError("");
    try {
      await api.del(`/submissions/${open.id}`);
      close();
      await load();
    } catch (e) { setError(apiErrorMessage(e, "Could not delete the submission.")); } finally { setBusy(false); }
  }

  const recipientChosen = shareKey === OTHER ? shareLabel.trim().length > 0 : shareKey !== "";

  async function share() {
    if (!open || !recipientChosen) return;
    setBusy(true); setError(""); setCopied("");
    try {
      const res = await api.post<{ url: string; passKey: string; share: { recipientLabel: string } }>(
        `/submissions/${open.id}/shares`,
        shareKey === OTHER
          ? { recipientLabel: shareLabel.trim(), expiresInDays: 30 }
          : { authorityKey: shareKey, expiresInDays: 30 },
      );
      setCredentials({ url: res.url, passKey: res.passKey, recipientLabel: res.share.recipientLabel });
      const sh = await api.get<{ shares: Share[] }>(`/submissions/${open.id}/shares`);
      setShares(sh.shares);
    } catch (e) { setError(apiErrorMessage(e, "Could not create the share link.")); } finally { setBusy(false); }
  }

  async function withdraw(id: string) {
    if (!open) return;
    setBusy(true); setError("");
    try {
      await api.del(`/submissions/shares/${id}`);
      const sh = await api.get<{ shares: Share[] }>(`/submissions/${open.id}/shares`);
      setShares(sh.shares);
    } catch (e) { setError(apiErrorMessage(e, "Could not withdraw the link.")); } finally { setBusy(false); }
  }

  // Leave the form without saving: the view shows the record as stored.
  function cancelEdit() {
    if (!open) return;
    setMeta(metaOf(open));
    setPayload(Object.fromEntries(Object.entries(open.payload ?? {}).map(([k, v]) => [k, v === null || v === undefined ? "" : String(v)])));
    if ((meta?.regime ?? "") !== (open.regime ?? "")) void loadFields(open.kind, open.regime ?? "");
    setEditing(false);
    setError("");
  }

  function copy(what: "link" | "key", value: string) {
    navigator.clipboard?.writeText(value).then(() => setCopied(what)).catch(() => {});
  }

  // The API draws the submission in the organisation's own look — the uploaded Word template's
  // colours, else its brand — so what the authority receives is the organisation's document.
  async function downloadPdf(s: Submission) {
    setDownloading(s.id); setError("");
    try {
      const blob = await apiBlob(`/submissions/${s.id}/document`);
      saveBlob(blob, `${fileNameSafe(`${s.displayId !== null ? `SUB-${s.displayId} ` : ""}${words(s.kind)} ${s.authority}`)}.pdf`);
    } catch (e) {
      setError(apiErrorMessage(e, "Could not produce the PDF."));
    } finally { setDownloading(null); }
  }

  // The next report on the same incident, prefilled from this one (POST /submissions/:id/follow-up).
  async function followUp(stage: "intermediate" | "final") {
    if (!open) return;
    setBusy(true); setError("");
    try {
      const res = await api.post<{ submission: Submission }>(`/submissions/${open.id}/follow-up`, { stage });
      await load();
      await openSubmission(res.submission);
    } catch (e) { setError(apiErrorMessage(e, "Could not draft the follow-up.")); } finally { setBusy(false); }
  }

  // ── Tiles, filters, search and sort ──────────────────────────────────────
  const all = submissions ?? [];
  const thisYear = new Date(now).getFullYear();
  const dueTime = (s: Submission) => (s.dueAt ? new Date(s.dueAt).getTime() : null);
  const tileTest: Record<TileKey, (s: Submission) => boolean> = {
    drafts: (s) => s.status === "draft",
    due24: (s) => { const t = dueTime(s); return isPending(s) && t !== null && t >= now && t - now <= 24 * 3_600_000; },
    overdue: (s) => { const t = dueTime(s); return isPending(s) && t !== null && t < now; },
    awaiting: (s) => s.status === "submitted",
    ackYear: (s) => s.status === "acknowledged" && !!s.acknowledgedAt && new Date(s.acknowledgedAt).getFullYear() === thisYear,
  };
  const chasing = all.filter((s) => s.acknowledgement?.chase).length;

  const shown = useMemo(() => {
    const rows = (submissions ?? []).filter((s) =>
      (!tile || tileTest[tile](s))
      && (!statusFilter || s.status === statusFilter)
      && (!kindFilter || s.kind === kindFilter)
      && matchesSearch(search, subRef(s), s.authority, s.regime, incidentName(s.incident), words(s.kind)));
    const cmp = SORTS.find((o) => o.key === sort)?.compare;
    return cmp ? [...rows].sort(cmp) : rows;
    // tileTest is rebuilt each render from `now`; now is the dependency that matters.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submissions, tile, statusFilter, kindFilter, search, sort, now]);

  // The next deadline across every submission still to be sent — overdue first.
  const nextDue = all
    .filter((s) => isPending(s) && s.dueAt)
    .sort((a, b) => new Date(a.dueAt!).getTime() - new Date(b.dueAt!).getTime())[0] ?? null;

  function exportCsv() {
    downloadCsv("regulatory-submissions", [
      { header: "Reference", value: (s: Submission) => subRef(s) },
      { header: "Kind", value: (s) => words(s.kind) },
      { header: "Authority", value: (s) => s.authority },
      { header: "Regime", value: (s) => s.regime },
      { header: "Status", value: (s) => s.status },
      { header: "Became aware", value: (s) => day(s.awareAt) },
      { header: "Due", value: (s) => day(s.dueAt) },
      { header: "Complete (%)", value: (s) => (s.completeness?.hasFieldSet ? s.completeness.percent : "") },
      { header: "Sent", value: (s) => day(s.submittedAt) },
      { header: "Method", value: (s) => (s.method ? methodLabel(s.method) : "") },
      { header: "Authority's reference", value: (s) => s.reference },
      { header: "Acknowledged", value: (s) => day(s.acknowledgedAt) },
      { header: "Incident", value: (s) => incidentName(s.incident) },
      { header: "Created", value: (s) => day(s.createdAt) },
    ], shown);
  }

  if (submissions === null) return <LoadingBlock />;

  const live = detail ? liveCompleteness(detail.sections, payload) : null;
  const chip = (active: boolean): React.CSSProperties => ({
    padding: "4px 10px", borderRadius: 3, fontSize: text.hint.fontSize, cursor: "pointer", fontFamily: theme.font,
    border: active ? `2px solid ${theme.color.accent}` : `1px solid ${theme.color.border}`,
    background: active ? theme.color.accentBg : theme.color.panel,
    color: active ? theme.color.accentText : theme.color.subtle, fontWeight: active ? 600 : 400,
  });

  return (
    <div className="page-pad">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20, gap: 16, flexWrap: "wrap" }}>
        <div>
          <h1 style={pageHeading}>Regulator returns</h1>
          <p style={{ ...pageSubtitle, maxWidth: 660 }}>
            Notifications, registrations and returns, with the fields each regime names and the
            clock running from awareness. Nothing is sent from here — no regulator publishes an
            inbound API — so this produces the artefact and records that you sent it.
          </p>
          {nextDue && (
            <button
              type="button"
              onClick={() => void openSubmission(nextDue)}
              title="The next deadline across every submission not yet sent"
              style={{ marginTop: 8, display: "inline-flex", alignItems: "center", gap: 8, background: "none", border: "none", padding: 0, cursor: "pointer", fontFamily: theme.font }}
            >
              <span style={text.hint}>Next deadline:</span>
              <span style={{ ...text.hint, fontWeight: 600, color: theme.color.heading }}>
                {subRef(nextDue) ? `${subRef(nextDue)} · ` : ""}{words(nextDue.kind)} to {nextDue.authority}
              </span>
              <CountdownChip dueAt={nextDue.dueAt!} now={now} dueSoonHours={dueSoonHours} />
            </button>
          )}
        </div>
        {canPrepare && (
          <button onClick={() => { setCreating(true); setError(""); }} style={{ ...btnPrimary, display: "flex", alignItems: "center", gap: 6 }}>
            <Plus size={15} /> New submission
          </button>
        )}
      </div>

      {error && <div style={{ ...card, marginBottom: 16, borderLeft: `3px solid ${theme.color.danger}`, color: theme.color.danger, fontSize: text.body.fontSize }}>{error}</div>}

      {submissions.length > 0 && (
        <>
          <SummaryTiles tiles={([
            { key: "drafts", label: "Drafts", tone: "neutral" },
            { key: "due24", label: "Due in 24 hours", tone: "warning" },
            { key: "overdue", label: "Overdue", tone: "danger" },
            { key: "awaiting", label: "Awaiting acknowledgement", tone: "accent", hint: chasing > 0 ? `${chasing} past the ${chaseDays}-day chase point` : undefined },
            { key: "ackYear", label: `Acknowledged in ${thisYear}`, tone: "success" },
          ] as const).map((t) => {
            const value = all.filter(tileTest[t.key]).length;
            return {
              ...t,
              value,
              tone: value === 0 && t.key !== "ackYear" ? "neutral" as const : t.tone,
              active: tile === t.key,
              onClick: () => setTile(tile === t.key ? null : t.key),
            };
          })} />
          <RegisterToolbar<Submission>
            search={search}
            onSearch={setSearch}
            placeholder="Search reference, authority, regime, incident"
            sortOptions={SORTS}
            sort={sort}
            onSort={setSort}
            onExport={exportCsv}
            count={{ shown: shown.length, total: all.length }}
          >
            {(tile || statusFilter || kindFilter || search) && (
              <button type="button" onClick={() => { setTile(null); setStatusFilter(""); setKindFilter(""); setSearch(""); }} style={{ ...secondaryBtn, padding: "6px 10px", fontSize: text.hint.fontSize }}>
                Clear filters
              </button>
            )}
          </RegisterToolbar>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", margin: "-4px 0 8px" }}>
            <button type="button" onClick={() => setStatusFilter("")} style={chip(statusFilter === "")}>Any status</button>
            {STATUSES.filter((st) => all.some((s) => s.status === st)).map((st) => (
              <button key={st} type="button" onClick={() => setStatusFilter(statusFilter === st ? "" : st)} style={chip(statusFilter === st)}>
                {words(st)} ({all.filter((s) => s.status === st).length})
              </button>
            ))}
          </div>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", margin: "0 0 12px" }}>
            <button type="button" onClick={() => setKindFilter("")} style={chip(kindFilter === "")}>All kinds</button>
            {KINDS.filter((k) => all.some((s) => s.kind === k)).map((k) => (
              <button key={k} type="button" onClick={() => setKindFilter(kindFilter === k ? "" : k)} style={chip(kindFilter === k)}>
                {words(k)} ({all.filter((s) => s.kind === k).length})
              </button>
            ))}
          </div>
        </>
      )}

      {submissions.length === 0 ? (
        <EmptyState
          title="No submissions"
          detail="Raise one here, or draft a full set from an incident — the Cyber incidents page fills in the figures the authority asks for."
        />
      ) : shown.length === 0 ? (
        <EmptyState title="No submissions match these filters" detail="Clear the filters to see every submission." />
      ) : (
        <div style={{ ...card, padding: 0, overflow: "hidden" }}>
          {shown.map((s, i) => (
            <div
              key={s.id}
              style={{
                display: "flex", alignItems: "center", gap: 8,
                borderTop: i === 0 ? "none" : `1px solid ${theme.color.border}`, paddingRight: 12,
              }}
            >
              <button
                onClick={() => void openSubmission(s)}
                style={{
                  display: "block", flex: 1, minWidth: 0, textAlign: "left", background: "transparent",
                  border: "none", padding: "12px 16px", cursor: "pointer",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                  {s.dueAt && isPending(s) && <CountdownChip dueAt={s.dueAt} now={now} dueSoonHours={dueSoonHours} />}
                  {s.displayId !== null && <span style={{ ...text.ref, color: theme.color.faint }}>SUB-{s.displayId}</span>}
                  <span style={{ ...text.itemTitle, flex: 1 }}>
                    {words(s.kind)} — {s.authority}
                  </span>
                  {s.completeness?.hasFieldSet && isPending(s) && (
                    <span title={`${s.completeness.filled} of ${s.completeness.required} required fields answered`} style={{ ...text.meta, fontWeight: 600, color: s.completeness.percent === 100 ? theme.color.successText : theme.color.warningText }}>
                      {s.completeness.percent}% complete
                    </span>
                  )}
                  <StatusPill value={s.status} />
                </div>
                <div style={{ ...text.hint, marginTop: 4 }}>
                  {s.regime && <>{s.regime} · </>}
                  {s.incident && <>{incidentName(s.incident)} · </>}
                  {s.dueAt && <>due {fmtDateTime(s.dueAt)} · </>}
                  {s.issues.length > 0 && isPending(s) && <span style={{ color: theme.color.warning }}>{s.issues.length} field{s.issues.length === 1 ? "" : "s"} missing · </span>}
                  {s.acknowledgement?.chase && <span style={{ color: theme.color.warningText }}>no acknowledgement after {s.acknowledgement.days} days · </span>}
                  {s.submittedAt ? <>sent {fmtDay(s.submittedAt)}{s.reference ? `, ref ${s.reference}` : ", no reference yet"}</> : "not sent"}
                </div>
              </button>
              <button
                type="button"
                onClick={() => void downloadPdf(s)}
                disabled={downloading === s.id}
                title="Download this submission as a PDF — the document you send the authority"
                style={{ ...secondaryBtn, display: "flex", alignItems: "center", gap: 6, flexShrink: 0, opacity: downloading === s.id ? 0.6 : 1 }}
              >
                <Download size={13} /> {downloading === s.id ? "Preparing…" : "PDF"}
              </button>
            </div>
          ))}
        </div>
      )}

      {creating && (
        <ModalOverlay onClose={() => setCreating(false)}>
          <ModalHeader title="New submission" onClose={() => setCreating(false)} />
          <div style={{ padding: 20 }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 12 }}>
              <Field label="Kind">
                <select value={newForm.kind} onChange={(e) => setNewForm({ ...newForm, kind: e.target.value })} style={input}>
                  {KINDS.map((k) => <option key={k} value={k}>{words(k)}</option>)}
                </select>
              </Field>
              <Field label="Authority" help="Who it goes to — the authorities of your Operating Locations, or another named by hand.">
                <select
                  value={newForm.authorityKey}
                  onChange={(e) => setNewForm({ ...newForm, authorityKey: e.target.value })}
                  style={input}
                  aria-label="Authority"
                >
                  <option value="">Who it goes to…</option>
                  <AuthorityOptions authorities={authorities} kindLabels={kindLabels} />
                  <option value={OTHER}>Another authority — name it</option>
                </select>
                {newForm.authorityKey === OTHER && (
                  <input
                    value={newForm.authority}
                    onChange={(e) => setNewForm({ ...newForm, authority: e.target.value })}
                    style={{ ...input, marginTop: 6 }}
                    placeholder="Name the authority, exactly as they are named"
                    autoFocus
                  />
                )}
              </Field>
              <Field label="Regime" help="Optional — adds any fields that regime names on top of the common set.">
                <input value={newForm.regime} onChange={(e) => setNewForm({ ...newForm, regime: e.target.value })} style={input} placeholder="e.g. Cyber Security (Jersey) Law 2026" />
              </Field>
            </div>
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 16 }}>
              <button onClick={() => setCreating(false)} style={secondaryBtn}>Cancel</button>
              <button onClick={create} disabled={busy} style={{ ...btnPrimary, opacity: busy ? 0.6 : 1 }}>Create</button>
            </div>
          </div>
        </ModalOverlay>
      )}

      {open && (
        <ModalOverlay onClose={close} wide>
          <ModalHeader
            title={`${open.displayId ? `SUB-${open.displayId} · ` : ""}${words(open.kind)} — ${open.authority}${editing ? " (editing)" : ""}`}
            onClose={close}
            right={
              <>
                <button
                  type="button"
                  onClick={() => void downloadPdf(open)}
                  disabled={downloading === open.id}
                  title="The submission as a PDF: the answers to the fields this regime names, the clock it answers and the reference — what you attach to the portal upload or the email"
                  style={{ ...secondaryBtn, display: "flex", alignItems: "center", gap: 6, opacity: downloading === open.id ? 0.6 : 1 }}
                >
                  <Download size={13} /> {downloading === open.id ? "Preparing…" : "PDF"}
                </button>
                {canChangeSubmission(perms, open.status) && (!isSendingChange(open.status) || open.canSend !== false) && !editing && open.status !== "acknowledged" && !(open.status === "withdrawn" && open.submittedAt) && <EditButton onClick={() => { setEditing(true); setError(""); }} />}
              </>
            }
          />
          <div style={{ padding: 20 }}>

            {(open.dueBasis || (open.dueAt && isPending(open))) && (
              <div style={{ ...text.hint, marginBottom: 12, display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                {open.dueAt && isPending(open) && <CountdownChip dueAt={open.dueAt} now={now} dueSoonHours={dueSoonHours} />}
                {open.dueBasis && (
                  <span style={{ display: "flex", gap: 6, alignItems: "flex-start" }}>
                    <Clock size={13} style={{ marginTop: 2, flexShrink: 0 }} />
                    <span>{open.dueBasis}</span>
                  </span>
                )}
              </div>
            )}

            {meta && <NextSteps items={submissionSteps(open, fields, payload, meta)} />}

            {wasSent(open) && (
              <div style={{ ...card, ...text.hint, marginBottom: 12, padding: "8px 12px" }}>
                {open.submittedAt && <>Sent {fmtDateTime(open.submittedAt)}{detail?.submittedByName ? ` by ${detail.submittedByName}` : ""}. </>}
                {open.acknowledgedAt && <>Acknowledged {fmtDateTime(open.acknowledgedAt)}{detail?.acknowledgedByName ? ` by ${detail.acknowledgedByName}` : ""}. </>}
                {open.status === "withdrawn"
                  ? "It was withdrawn after it was sent, so it stays a closed record. Draft a new submission if one is needed."
                  : open.status === "acknowledged"
                    ? "This is the record of what the authority was told and it is final. Correct it with a follow-up report."
                    : "This is the record of what the authority was told: only the acknowledgement and the authority's reference can still be recorded. Correct it with a follow-up report, or withdraw it if it was sent in error."}
              </div>
            )}

            {synced && <div style={{ marginBottom: 12 }}><SyncNotice sync={synced} onDismiss={() => setSynced(null)} /></div>}

            {!editing && (
              <>
                {detail && (detail.sections.length > 0 || detail.timeline.length > 0) && (
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", columnGap: 16, alignItems: "start" }}>
                    {detail.sections.length > 0 && (
                      <DetailSection title="Completeness">
                        <SubmissionChecklist sections={detail.sections} payload={payload} />
                      </DetailSection>
                    )}
                    {detail.timeline.length > 0 && (
                      <DetailSection title="History">
                        <SubmissionTimeline events={detail.timeline} />
                      </DetailSection>
                    )}
                  </div>
                )}

                <DetailSection title="The submission">
                  <DetailFields fields={[
                    { label: "Reference", value: subRef(open) },
                    { label: "Kind", value: words(open.kind) },
                    { label: "Status", value: <StatusPill value={open.status} /> },
                    { label: "Authority", value: open.authority },
                    { label: "Regime", value: open.regime },
                    { label: "Became aware at", value: open.awareAt ? fmtDateTime(open.awareAt) : null },
                    { label: "Due at", value: open.dueAt ? fmtDateTime(open.dueAt) : null },
                    { label: "Why it is due then", value: open.dueBasis },
                    { label: "Incident it reports", value: open.incident ? incidentName(open.incident) : (meta?.incidentId ? incidents.find((i) => i.id === meta.incidentId)?.name ?? "Linked incident" : null) },
                    ...(open.notification ? [{
                      label: "Duty it answers",
                      value: (
                        <span style={{ display: "inline-flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                          Answers the incident's notification to {open.notification.body}
                          <StatusPill value={open.notification.status} />
                          {open.notification.sentAt && <span style={text.meta}>recorded sent {fmtDateTime(open.notification.sentAt)}{open.notification.reference ? ` · ref ${open.notification.reference}` : ""}</span>}
                        </span>
                      ),
                      wide: true,
                    }] : []),
                    { label: "How it is sent", value: open.method ? methodLabel(open.method) : null },
                    { label: "Sent at", value: open.submittedAt ? fmtDateTime(open.submittedAt) : null },
                    { label: "Authority's reference", value: open.reference },
                    { label: "Acknowledged at", value: open.acknowledgedAt ? fmtDateTime(open.acknowledgedAt) : null },
                    { label: "Notes", value: open.notes, wide: true, multiline: true },
                  ]} />
                </DetailSection>

                <DetailSection title="What the authority asks for">
                  {fields.length === 0 ? (
                    <div style={{ ...text.hint, color: theme.color.faint }}>This kind of submission has no set form; attach what the authority asked for.</div>
                  ) : (
                    <DetailFields fields={fields.map((f): DetailField => {
                      const raw = (payload[f.key] ?? "").trim();
                      const missing = f.required && !raw;
                      return {
                        label: f.label + (f.required ? (missing ? " (required, still empty)" : "") : " (optional)"),
                        value: payloadValue(f, raw),
                        wide: f.kind === "longtext",
                        multiline: f.kind === "longtext",
                      };
                    })} />
                  )}
                </DetailSection>

                {meta?.incidentId && (
                  <RelatedRecords
                    entity={null}
                    links={[{ kindLabel: "Incident", label: incidents.find((i) => i.id === meta.incidentId)?.name ?? "Linked incident", href: KIND_HREFS.incident(meta.incidentId) }]}
                  />
                )}

                {live && live.missing.length > 0 && isPending(open) && (
                  <div style={{ ...text.hint, color: theme.color.warningText, marginTop: 12 }}>
                    {live.missing.length} required field{live.missing.length === 1 ? "" : "s"} still empty: {live.missing.join(", ")}.
                    It can be marked ready once they are answered. If the window is closing, download the
                    PDF and send what is known now — a late notification is worse than an incomplete one,
                    and most regimes expect an update to follow.
                  </div>
                )}

                {shares.length > 0 && (
                  <DetailSection title="Shared with the authority">
                    <div style={{ ...text.hint, display: "grid", gap: 4 }}>
                      {shares.map((sh) => (
                        <div key={sh.id} style={{ display: "flex", gap: 8, alignItems: "baseline", flexWrap: "wrap" }}>
                          <span style={{ color: theme.color.text }}>{sh.recipientLabel}</span>
                          <span style={{ color: sh.standing === "locked" ? theme.color.warning : theme.color.subtle }}>
                            {STANDING_LABEL[sh.standing]}
                            {sh.standing === "active" && ` until ${fmtDay(sh.expiresAt)}`}
                          </span>
                          {sh.viewCount > 0 && <span>viewed {sh.viewCount}×{sh.lastViewedAt ? `, last ${fmtDateTime(sh.lastViewedAt)}` : ""}</span>}
                        </div>
                      ))}
                    </div>
                  </DetailSection>
                )}

                {canEdit && (() => {
                  // Sending is per submission: the role may hold submissions:send for some authorities only.
                  const canSendThis = canSend && open.canSend !== false;
                  const followUps = canPrepare && !!detail && detail.followUpKinds.length > 0 && (open.status === "submitted" || open.status === "acknowledged");
                  const sharable = canSendThis && (open.status === "submitted" || open.status === "acknowledged" || open.status === "ready");
                  const liveShares = shares.filter((sh) => sh.standing !== "withdrawn");
                  const reopenable = open.status === "withdrawn" && !open.submittedAt;
                  const hasStatusAction = open.status !== "acknowledged" && !(open.status === "withdrawn" && open.submittedAt)
                    && (open.status === "draft" || open.status === "withdrawn" ? canPrepare : canSendThis || (open.status === "ready" && canPrepare));
                  if (!hasStatusAction && !followUps && !sharable) return null;
                  return (
                    <RecordActions note={open.status === "ready"
                      ? (canSend && !canSendThis
                        ? "Your role sends only to data protection authorities — someone else records this one as sent."
                        : "Record it as sent once it has gone. Edit it first to record how it was sent and the authority's reference.")
                      : undefined}>
                      {open.status === "draft" && canPrepare && (
                        <button
                          onClick={() => void save("ready")}
                          disabled={busy || (!!live && live.missing.length > 0)}
                          title={live && live.missing.length > 0 ? `Answer the required fields first: ${live.missing.join(", ")}` : "Every required field is answered"}
                          style={{ ...btnPrimary, opacity: busy || (live && live.missing.length > 0) ? 0.6 : 1 }}
                        >
                          Mark ready
                        </button>
                      )}
                      {open.status === "ready" && canSendThis && (
                        <button onClick={() => void save("submitted")} disabled={busy} style={{ ...btnPrimary, display: "flex", alignItems: "center", gap: 6 }}>
                          <Send size={14} /> Record as sent
                        </button>
                      )}
                      {open.status === "submitted" && canSendThis && <button onClick={() => void save("acknowledged")} disabled={busy} style={btnPrimary} title="The authority has confirmed receipt. This closes the record.">Record acknowledgement</button>}
                      {reopenable && canPrepare && <button onClick={() => void save("draft")} disabled={busy} style={secondaryBtn}>Reopen as draft</button>}
                      {(open.status === "draft" || open.status === "ready") && canPrepare && (
                        <button onClick={() => { if (window.confirm("Withdraw this submission? It can be reopened as a draft later.")) void save("withdrawn"); }} disabled={busy} style={dangerBtn}>Withdraw</button>
                      )}
                      {open.status === "submitted" && canSendThis && (
                        <button onClick={() => { if (window.confirm("Withdraw this submission as sent in error? It stays on record as sent and withdrawn, and cannot be reopened.")) void save("withdrawn"); }} disabled={busy} style={dangerBtn}>Withdraw (sent in error)</button>
                      )}
                      {(isPending(open) || reopenable) && canPrepare && (
                        <button onClick={() => void remove()} disabled={busy} style={dangerBtn}>Delete</button>
                      )}

                      {followUps && (
                        <div style={{ flexBasis: "100%", display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", paddingTop: 8, borderTop: `1px solid ${theme.color.border}` }}>
                          <CornerDownRight size={14} color={theme.color.accent} />
                          <span style={{ ...text.hint, flex: "1 1 260px" }}>
                            Most regimes expect an update and a final report after the first notification. Draft the next one from this, linked to the same incident.
                          </span>
                          <button type="button" disabled={busy} onClick={() => void followUp("intermediate")} style={secondaryBtn}>Duplicate as intermediate report</button>
                          <button type="button" disabled={busy} onClick={() => void followUp("final")} style={secondaryBtn}>Duplicate as final report</button>
                        </div>
                      )}

                      {sharable && (
                        <div style={{ flexBasis: "100%", paddingTop: 8, borderTop: `1px solid ${theme.color.border}` }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
                            <Link2 size={14} color={theme.color.accent} />
                            <span style={{ ...text.itemTitle, margin: 0 }}>Share with the authority</span>
                          </div>
                          <div style={{ ...text.hint, marginBottom: 8 }}>
                            A read-only link to this submission alone — no profile, no other submission, no
                            evidence. It opens only with the pass key issued beside it, so send the two in
                            separate emails. Expires in 30 days and can be withdrawn.
                          </div>
                          <div style={{ display: "flex", gap: 8, alignItems: "flex-start", flexWrap: "wrap" }}>
                            <select
                              value={shareKey}
                              onChange={(e) => { setShareKey(e.target.value); setCredentials(null); }}
                              style={{ ...input, flex: "1 1 320px", minWidth: 220 }}
                              aria-label="Who the link is for"
                            >
                              <option value="">Who it is for…</option>
                              <AuthorityOptions authorities={authorities} kindLabels={kindLabels} />
                              <option value={OTHER}>Another authority — name it</option>
                            </select>
                            {shareKey === OTHER && (
                              <input
                                value={shareLabel}
                                onChange={(e) => setShareLabel(e.target.value)}
                                style={{ ...input, flex: "1 1 220px" }}
                                placeholder="Name the authority"
                                autoFocus
                              />
                            )}
                            <button onClick={share} disabled={busy || !recipientChosen} style={{ ...secondaryBtn, opacity: busy || !recipientChosen ? 0.6 : 1 }}>Create link</button>
                          </div>
                          {authorities.length === 0 && (
                            <div style={{ ...text.meta, marginTop: 6 }}>
                              No authorities listed yet — add your Operating Locations on My Organisation and
                              the authorities of each jurisdiction appear here.
                            </div>
                          )}
                          {shareKey && shareKey !== OTHER && (
                            <div style={{ ...text.meta, marginTop: 6 }}>
                              {authorities.find((a) => a.key === shareKey)?.basis}
                            </div>
                          )}

                          {credentials && (
                            <div style={{ ...card, marginTop: 10, padding: 12 }}>
                              <div style={{ ...text.itemTitle, marginBottom: 2 }}>
                                Shown once — copy both now
                              </div>
                              <div style={{ ...text.hint, marginBottom: 10 }}>
                                Email the link to {credentials.recipientLabel}, then send the pass key in a
                                second email. Neither half opens the submission on its own, so a forwarded
                                email does not hand it on. Neither can be shown again — if the pass key is
                                lost, withdraw this link and create another.
                              </div>
                              <CredentialRow
                                label="Link — first email"
                                icon={<Link2 size={13} color={theme.color.accent} />}
                                value={credentials.url}
                                copied={copied === "link"}
                                onCopy={() => copy("link", credentials.url)}
                              />
                              <CredentialRow
                                label="Pass key — second email"
                                icon={<KeyRound size={13} color={theme.color.accent} />}
                                value={credentials.passKey}
                                copied={copied === "key"}
                                onCopy={() => copy("key", credentials.passKey)}
                                large
                              />
                            </div>
                          )}

                          {liveShares.length > 0 && (
                            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 10 }}>
                              {liveShares.map((sh) => (
                                <button key={sh.id} type="button" onClick={() => void withdraw(sh.id)} disabled={busy} style={{ ...secondaryBtn, padding: "4px 10px", fontSize: text.hint.fontSize }}>
                                  Withdraw link for {sh.recipientLabel}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </RecordActions>
                  );
                })()}

                <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 16 }}>
                  <button onClick={close} style={secondaryBtn}>Close</button>
                </div>
              </>
            )}

            {editing && meta && wasSent(open) && (
              <>
                <Field label="Authority's reference" help="What comes back once it is received.">
                  <input value={meta.reference} onChange={(e) => setMeta({ ...meta, reference: e.target.value })} style={input} />
                </Field>
                <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 16, flexWrap: "wrap" }}>
                  <button onClick={cancelEdit} disabled={busy} style={secondaryBtn}>Cancel</button>
                  <button onClick={() => void save()} disabled={busy} style={{ ...btnPrimary, opacity: busy ? 0.6 : 1 }}>Save</button>
                </div>
              </>
            )}

            {editing && meta && !wasSent(open) && (
              <>
                <div style={text.sectionTitle}>The submission</div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: "12px 16px", marginBottom: 16 }}>
                  <Field label="Authority" help="Who it goes to, exactly as they are named.">
                    <input value={meta.authority} onChange={(e) => setMeta({ ...meta, authority: e.target.value })} style={input} />
                  </Field>
                  <Field label="Regime (optional)" help="Adds the fields that regime names on top of the common set.">
                    <input value={meta.regime} onChange={(e) => setMeta({ ...meta, regime: e.target.value })} onBlur={() => void loadFields(open.kind, meta.regime)} style={input} />
                  </Field>
                  <Field label="Became aware at" help="The statutory clock runs from here.">
                    <input type="datetime-local" value={meta.awareAt} onChange={(e) => setMeta({ ...meta, awareAt: e.target.value })} style={input} />
                  </Field>
                  <Field label="Due at">
                    <input type="datetime-local" value={meta.dueAt} onChange={(e) => setMeta({ ...meta, dueAt: e.target.value })} style={input} />
                  </Field>
                  <Field label="Why it is due then" help="The provision that sets the clock.">
                    <input value={meta.dueBasis} onChange={(e) => setMeta({ ...meta, dueBasis: e.target.value })} style={input} />
                  </Field>
                  <Field label="Incident it reports">
                    <select value={meta.incidentId} onChange={(e) => setMeta({ ...meta, incidentId: e.target.value })} style={input}>
                      <option value="">None</option>
                      {meta.incidentId && !incidents.some((i) => i.id === meta.incidentId) && <option value={meta.incidentId}>Linked incident</option>}
                      {incidents.map((i) => <option key={i.id} value={i.id}>{i.name}</option>)}
                    </select>
                  </Field>
                  <Field label="How it is sent">
                    <select value={meta.method} onChange={(e) => setMeta({ ...meta, method: e.target.value })} style={input}>
                      <option value="">Not recorded</option>
                      {METHODS.map((m) => <option key={m} value={m}>{methodLabel(m)}</option>)}
                    </select>
                  </Field>
                  <Field label="Authority's reference" help="What comes back once it is received.">
                    <input value={meta.reference} onChange={(e) => setMeta({ ...meta, reference: e.target.value })} style={input} />
                  </Field>
                  <Field label="Notes" span2>
                    <textarea value={meta.notes} onChange={(e) => setMeta({ ...meta, notes: e.target.value })} style={textarea} rows={2} />
                  </Field>
                </div>
                <div style={text.sectionTitle}>What the authority asks for</div>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px 16px" }}>
                  {fields.map((f) => {
                    const missing = f.required && !(payload[f.key] ?? "").trim();
                    const fieldStyle = missing ? { ...input, borderColor: theme.color.warning } : input;
                    return (
                      <Field key={f.key} label={f.label + (f.required ? (missing ? " (required, still empty)" : "") : " (optional)")} help={f.basis} span2={f.kind === "longtext"}>
                        {f.kind === "longtext" ? (
                          <textarea value={payload[f.key] ?? ""} onChange={(e) => setPayload({ ...payload, [f.key]: e.target.value })} style={missing ? { ...textarea, borderColor: theme.color.warning } : textarea} />
                        ) : f.kind === "boolean" ? (
                          <select value={payload[f.key] ?? ""} onChange={(e) => setPayload({ ...payload, [f.key]: e.target.value })} style={fieldStyle}>
                            <option value="">Not answered</option>
                            <option value="true">Yes</option>
                            <option value="false">No</option>
                          </select>
                        ) : f.kind === "choice" && f.choices?.length ? (
                          <select value={payload[f.key] ?? ""} onChange={(e) => setPayload({ ...payload, [f.key]: e.target.value })} style={fieldStyle}>
                            <option value="">Not answered</option>
                            {f.choices.map((c) => <option key={c} value={c}>{words(c)}</option>)}
                          </select>
                        ) : f.kind === "date" ? (
                          <input
                            type="datetime-local"
                            value={dateForInput(payload[f.key] ?? "")}
                            onChange={(e) => setPayload({ ...payload, [f.key]: fromInput(e.target.value) ?? "" })}
                            style={fieldStyle}
                          />
                        ) : (
                          <input
                            type={f.kind === "number" ? "number" : "text"}
                            value={payload[f.key] ?? ""}
                            onChange={(e) => setPayload({ ...payload, [f.key]: e.target.value })}
                            style={fieldStyle}
                          />
                        )}
                      </Field>
                    );
                  })}
                </div>

                {live && live.missing.length > 0 && isPending(open) && (
                  <div style={{ ...text.hint, color: theme.color.warningText, marginTop: 12 }}>
                    {live.missing.length} required field{live.missing.length === 1 ? "" : "s"} still empty: {live.missing.join(", ")}.
                    It can be marked ready once they are answered.
                  </div>
                )}

                <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 16, flexWrap: "wrap" }}>
                  <button onClick={cancelEdit} disabled={busy} style={secondaryBtn}>Cancel</button>
                  <button onClick={() => void save()} disabled={busy} style={{ ...btnPrimary, opacity: busy ? 0.6 : 1 }}>{open.status === "draft" ? "Save draft" : "Save"}</button>
                </div>
              </>
            )}
          </div>
        </ModalOverlay>
      )}
    </div>
  );
}

/** One answer as the view shows it: Yes / No, the choice in words, a date formatted. */
function payloadValue(f: FieldDef, raw: string): React.ReactNode {
  if (!raw) return null;
  if (f.kind === "boolean") return raw === "true" ? "Yes" : raw === "false" ? "No" : raw;
  if (f.kind === "choice") return words(raw);
  if (f.kind === "date" && !Number.isNaN(Date.parse(raw))) return fmtDateTime(raw);
  return raw;
}

/** The countries present in the catalogue, in the order the backend returned them. */
function countriesOf(authorities: Authority[]): string[] {
  return authorities.reduce<string[]>((acc, a) => (acc.includes(a.country) ? acc : [...acc, a.country]), []);
}

/** The catalogue as option groups, one per country — the new-submission form and the share dialog. */
function AuthorityOptions({ authorities, kindLabels }: { authorities: Authority[]; kindLabels: Record<string, string> }) {
  return (
    <>
      {countriesOf(authorities).map((country) => (
        <optgroup key={country} label={country}>
          {authorities.filter((a) => a.country === country).map((a) => (
            <option key={a.key} value={a.key}>
              {a.name} — {kindLabels[a.kind] ?? a.kind}
            </option>
          ))}
        </optgroup>
      ))}
    </>
  );
}

function CredentialRow({ label, icon, value, copied, onCopy, large }: {
  label: string; icon: React.ReactNode; value: string; copied: boolean; onCopy: () => void; large?: boolean;
}) {
  return (
    <div style={{ marginBottom: 8 }}>
      <div style={{ ...text.label, display: "flex", alignItems: "center", gap: 6, marginBottom: 3 }}>
        {icon}{label}
      </div>
      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        <code style={{
          flex: 1, minWidth: 0, padding: "6px 8px", background: "var(--c-panel)", border: `1px solid ${theme.color.border}`,
          borderRadius: 3, fontFamily: text.mono.fontFamily, fontSize: large ? text.itemTitle.fontSize : text.meta.fontSize, letterSpacing: large ? "1px" : undefined,
          fontWeight: large ? 700 : 400, color: theme.color.heading, wordBreak: "break-all",
        }}>{value}</code>
        <button onClick={onCopy} style={{ ...secondaryBtn, display: "flex", alignItems: "center", gap: 5 }}>
          {copied ? <Check size={13} /> : <Copy size={13} />}{copied ? "Copied" : "Copy"}
        </button>
      </div>
    </div>
  );
}
